import React, { useEffect, useState } from 'react';
import api from '../services/ApiService';

type QueueSession = {
  id: number;
  tenant_id: number;
  user_id: number;
  username: string;
  queue_id: number;
  queue_name: string;
  status: string;
  login_at?: string;
  logout_at?: string;
  updated_at?: string;
};

export default function QueueMonitor() {
  const [sessions, setSessions] = useState<QueueSession[]>([]);
  const [loading, setLoading] = useState(true);

  const loadSessions = async () => {
    try {
      const data = await api.getQueueSessions();
      setSessions(data || []);
    } catch (e) {
      console.error(e);
      alert('Failed to load queue sessions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSessions();

    const timer = setInterval(() => {
      loadSessions();
    }, 3000);

    return () => clearInterval(timer);
  }, []);

  const loggedIn = sessions.filter((s) => s.status === 'logged_in').length;
  const loggedOut = sessions.filter((s) => s.status === 'logged_out').length;
  const uniqueQueues = new Set(sessions.map((s) => s.queue_name)).size;
  const uniqueAgents = new Set(sessions.map((s) => s.user_id)).size;

  return (
    <>
      <div className="admin-page-title">Queue Monitor</div>

      <div className="admin-page-subtitle">
        Tenant-safe queue staffing, login/logout status, and agent queue occupancy.
      </div>

      <div className="dashboard-grid">
        <StatCard title="Queue Sessions" value={sessions.length} />
        <StatCard title="Logged In" value={loggedIn} />
        <StatCard title="Logged Out" value={loggedOut} />
        <StatCard title="Queues" value={uniqueQueues} />
        <StatCard title="Agents" value={uniqueAgents} />
      </div>

      <div className="content-card">
        <div className="admin-card-header">
          <h3>Queue Sessions</h3>

          <button className="secondary-btn" onClick={loadSessions}>
            Refresh
          </button>
        </div>

        {loading && (
          <p className="admin-muted">Loading queue sessions...</p>
        )}

        {!loading && sessions.length === 0 && (
          <p className="admin-muted">
            No queue login sessions found for this tenant.
          </p>
        )}

        {sessions.length > 0 && (
          <table className="admin-table">
            <thead>
              <tr>
                <th>Queue</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Login Time</th>
                <th>Logout Time</th>
                <th>Last Update</th>
              </tr>
            </thead>

            <tbody>
              {sessions.map((s) => (
                <tr key={s.id}>
                  <td>
                    <strong>{s.queue_name}</strong>
                    <div className="admin-muted">
                      Queue ID {s.queue_id}
                    </div>
                  </td>

                  <td>
                    <strong>{s.username}</strong>
                    <div className="admin-muted">
                      Tenant {s.tenant_id}
                    </div>
                  </td>

                  <td>
                    <span
                      className={`admin-pill ${
                        s.status === 'logged_in' ? 'green' : 'red'
                      }`}
                    >
                      {s.status}
                    </span>
                  </td>

                  <td>{s.login_at || '-'}</td>
                  <td>{s.logout_at || '-'}</td>
                  <td>{s.updated_at || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function StatCard({ title, value }: any) {
  return (
    <div className="stat-card">
      <div className="stat-title">{title}</div>
      <div className="stat-value">{value}</div>
    </div>
  );
}